/* eslint-disable react-hooks/exhaustive-deps */
// -----------------------------------------------------------------------------
// AttendedChecklistV2
//
// Renders the Attended Checklist surface on the lead-detail page using V2
// components (ChecklistItemRow). Replaces the V1 ChecklistTab inline + tab
// surfaces in OpenAttendedPopup / OpenConsultationPopup / OverView.
//
// Visibility gate (managed by parent via the `onVisibilityChange` callback or
// the exported `useAttendedChecklistVisibility` hook):
//   - The lead has at least one lead_treatment in CONSULTATION_ATTENDED, AND
//   - There's a V2 task whose template applies_to_statuses includes
//     CONSULTATION_ATTENDED AND that task has checklist cells.
//
// Data shape — uses two existing endpoints (no new BE):
//   1. /api/v1/practices/{p}/leads/{l}/task-checklists/  → grouped by template
//   2. /api/v1/practices/{p}/task-templates/?limit=200    → for applies_to_statuses
//
// We can't filter by `applies_to_status` server-side (no such param exists), so
// we fetch all groups and filter client-side using the templates lookup.
// Tasks within each surfaced group are rendered with title + ChecklistItemRow
// for each checklist cell, sorted by due_at.
// -----------------------------------------------------------------------------
import PropTypes from 'prop-types';
import { useState, useEffect, useMemo, useCallback } from 'react';
import * as Sentry from '@sentry/react';
import { useSelector } from 'src/redux/store';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Card,
    Typography,
    LinearProgress,
    Stack,
    Skeleton,
    Alert,
    Button,
    Chip,
    Divider,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { callApi } from 'src/utils/apiCall';
import { hasFeatureAccess } from 'src/utils/featurePermission';
import { useAuth } from 'src/hooks';
import { primary } from 'src/theme/palette';
import ChecklistItemRow from 'src/sections/tasks/ChecklistItemRow';

const ATTENDED_STATUS = 'CONSULTATION_ATTENDED';

// Hook: parent uses this to decide whether to render the surface at all.
// Returns { ready, visible, leadInAttended, hasAttendedTask }.
export function useAttendedChecklistVisibility({ practiceId, leadId, leadDetail }) {
    const [state, setState] = useState({
        ready: false,
        visible: false,
        leadInAttended: false,
        hasAttendedTask: false,
        debug: null, // populated for superadmin-only diagnostic tab
    });
    // #215 (I-NEW-2) — React to template edits. The taskTemplate slice bumps
    // `templatesUpdatedAt` on every mutation (create/update/delete/archive/
    // clone/revert). Subscribing here re-runs the visibility check so an
    // admin's template edit immediately shows/hides the checklist surface
    // without a full page reload.
    const templatesUpdatedAt = useSelector(
        (s) => s?.taskTemplate?.templatesUpdatedAt || 0
    );

    useEffect(() => {
        let cancelled = false;
        const run = async () => {
            if (!practiceId || !leadId) {
                setState({ ready: true, visible: false, leadInAttended: false, hasAttendedTask: false });
                return;
            }
            const treatments = leadDetail?.lead_treatments || [];
            const leadInAttended = treatments.some((t) => t.status === ATTENDED_STATUS);

            if (!leadInAttended) {
                if (!cancelled) {
                    setState({ ready: true, visible: false, leadInAttended: false, hasAttendedTask: false });
                }
                return;
            }
            if (!hasFeatureAccess('tasks-v2', practiceId)) {
                if (!cancelled) {
                    setState({ ready: true, visible: false, leadInAttended, hasAttendedTask: false, debug: { featureAccess: false } });
                }
                return;
            }
            try {
                const [groupsRes, tplRes] = await Promise.all([
                    callApi(`/api/v1/practices/${practiceId}/leads/${leadId}/task-checklists/`),
                    callApi(`/api/v1/practices/${practiceId}/task-templates/?limit=200`),
                ]);
                let groups = [];
                if (Array.isArray(groupsRes?.data)) groups = groupsRes.data;
                else if (Array.isArray(groupsRes?.data?.results)) groups = groupsRes.data.results;
                let tplList = [];
                if (Array.isArray(tplRes?.data?.results)) tplList = tplRes.data.results;
                else if (Array.isArray(tplRes?.data)) tplList = tplRes.data;
                const tplStatuses = new Map();
                const tplTreatments = new Map();
                tplList.forEach((t) => {
                    tplStatuses.set(String(t.id), Array.isArray(t.applies_to_statuses) ? t.applies_to_statuses : []);
                    tplTreatments.set(String(t.id), Array.isArray(t.applies_to_treatment_items) ? t.applies_to_treatment_items : []);
                });
                // 4th condition: resolve the lead's active treatment id.
                // Priority: ATTENDED → BOOKED → first treatment (handles all status variants).
                const allTreatments = leadDetail?.lead_treatments || [];
                const matchedTreatmentV = allTreatments.find((t) => t.status === ATTENDED_STATUS) ||
                    allTreatments.find((t) => t.status === 'CONSULTATION_BOOKED') ||
                    allTreatments[0];
                // treatment_data.treatment_item.id is the master TreatmentItem ID (small numbers,
                // matches applies_to_treatment_items[].id). treatment_data.id is the
                // PracticeTreatmentItem ID (practice-specific, large numbers) — wrong ID space.
                // Only treatment_item.id is the correct master TreatmentItem ID.
                // The other fields are different ID spaces; we no longer fall
                // back to them because that would silently mis-match.
                const leadTreatmentId = matchedTreatmentV?.treatment_data?.treatment_item?.id ?? null;
                // lead_treatment row id — prevents tasks from a different treatment
                // row bleeding in when two treatments share the same treatment_item.id.
                const matchedLeadTreatmentId = matchedTreatmentV?.id ?? null;
                const attendedTemplates = tplList.filter((t) => (Array.isArray(t.applies_to_statuses) ? t.applies_to_statuses : []).includes(ATTENDED_STATUS));
                // Filter groups by template match AND task-level lead_treatment match.
                // Generic tasks (no lead_treatment) always pass the task-level filter.
                const matchingGroups = groups.map((g) => {
                    const statuses = tplStatuses.get(String(g.template_id)) || [];
                    if (!statuses.includes(ATTENDED_STATUS)) return null;
                    const treatments = tplTreatments.get(String(g.template_id)) || [];
                    const isGeneric = treatments.length === 0;
                    const isTreatmentMatch = leadTreatmentId != null && treatments.some((ti) => String(ti?.id) === String(leadTreatmentId));
                    if (!isGeneric && !isTreatmentMatch) return null;
                    // Trim tasks to those belonging to the active lead_treatment row.
                    // lead_treatment is a nested object — use .id to extract the row id.
                    // Generic tasks (no lead_treatment) always pass.
                    const tasksForThisTx = (g.tasks || []).filter((task) =>
                        task.lead_treatment == null
                        || String(task.lead_treatment?.id) === String(matchedLeadTreatmentId)
                    );
                    return tasksForThisTx.length > 0 ? { ...g, tasks: tasksForThisTx } : null;
                }).filter(Boolean);
                const groupsWithItems = matchingGroups.filter((g) => (g.tasks || []).some((task) => (task.checklist_items || []).length > 0));
                const hasAttendedTask = groupsWithItems.length > 0;
                const debug = {
                    featureAccess: true,
                    groupsCount: groups.length,
                    templatesCount: tplList.length,
                    attendedTemplatesCount: attendedTemplates.length,
                    matchingGroupsCount: matchingGroups.length,
                    groupsWithItemsCount: groupsWithItems.length,
                    sampleTask: matchingGroups[0]?.tasks?.[0] ? {
                        id: matchingGroups[0].tasks[0].id,
                        title: matchingGroups[0].tasks[0].title,
                        keys: Object.keys(matchingGroups[0].tasks[0]),
                        checklist_items_len: (matchingGroups[0].tasks[0].checklist_items || []).length,
                    } : null,
                };
                if (!cancelled) {
                    setState({ ready: true, visible: hasAttendedTask, leadInAttended, hasAttendedTask, debug });
                }
            } catch (err) {
                Sentry.captureException(err, { tags: { context: 'attended_checklist_v2_visibility' } });
                if (!cancelled) {
                    setState({ ready: true, visible: false, leadInAttended, hasAttendedTask: false, debug: { error: err?.message || 'fetch failed' } });
                }
            }
        };
        run();
        return () => {
            cancelled = true;
        };
    }, [practiceId, leadId, JSON.stringify((leadDetail?.lead_treatments || []).map((t) => ({ id: t.id, s: t.status, ti: t.treatment_data?.treatment_item?.id ?? null }))), templatesUpdatedAt]);

    return state;
}

// -----------------------------------------------------------------------------

export default function AttendedChecklistV2({
    practiceId,
    leadId,
    leadDetail,
    compact,
    onVisibilityChange,
}) {
    const { user } = useAuth();
    const isSuperuser = !!user?.is_superuser;

    const [groups, setGroups] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [fetchError, setFetchError] = useState(null);

    const treatments = leadDetail?.lead_treatments || [];
    const leadInAttended = treatments.some((t) => t.status === ATTENDED_STATUS);

    const fetchAll = useCallback(async () => {
        if (!practiceId || !leadId) return;
        // Match the BE gate on `tasks-v2` so users without the feature don't
        // generate 403s on /task-checklists/ + /task-templates/ each time a
        // lead-detail page mounts. practiceId-aware to close the
        // practice-switch window leak.
        if (!hasFeatureAccess('tasks-v2', practiceId)) {
            setGroups([]);
            setTemplates([]);
            setLoading(false);
            return;
        }
        setLoading(true);
        setFetchError(null);
        try {
            const [groupsRes, tplRes] = await Promise.all([
                callApi(`/api/v1/practices/${practiceId}/leads/${leadId}/task-checklists/`),
                callApi(`/api/v1/practices/${practiceId}/task-templates/?limit=200`),
            ]);
            let groupsData = [];
            if (Array.isArray(groupsRes?.data)) groupsData = groupsRes.data;
            else if (Array.isArray(groupsRes?.data?.results)) groupsData = groupsRes.data.results;
            let tplData = [];
            if (Array.isArray(tplRes?.data?.results)) tplData = tplRes.data.results;
            else if (Array.isArray(tplRes?.data)) tplData = tplRes.data;
            setGroups(groupsData);
            setTemplates(tplData);
        } catch (err) {
            Sentry.captureException(err, { tags: { context: 'attended_checklist_v2_fetch' } });
            setFetchError('Failed to load attended checklist.');
        } finally {
            setLoading(false);
        }
    }, [practiceId, leadId]);

    useEffect(() => {
        fetchAll();
    }, [fetchAll]);

    const tplStatusMap = useMemo(() => {
        const m = new Map();
        templates.forEach((t) => m.set(String(t.id), Array.isArray(t.applies_to_statuses) ? t.applies_to_statuses : []));
        return m;
    }, [templates]);

    const tplTreatmentsMap = useMemo(() => {
        const m = new Map();
        templates.forEach((t) => m.set(String(t.id), Array.isArray(t.applies_to_treatment_items) ? t.applies_to_treatment_items : []));
        return m;
    }, [templates]);

    // Resolve the lead's active treatment id.
    // Priority: ATTENDED → BOOKED → first treatment (handles all status variants).
    // Use treatment_data.treatment_item.id (master TreatmentItem ID, small numbers) —
    // this is the same ID space as applies_to_treatment_items[].id on templates.
    // treatment_data.id is the PracticeTreatmentItem ID (large, practice-specific) — wrong space.
    const leadTreatmentId = useMemo(() => {
        const ts = leadDetail?.lead_treatments || [];
        const matched = ts.find((t) => t.status === ATTENDED_STATUS) ||
            ts.find((t) => t.status === 'CONSULTATION_BOOKED') ||
            ts[0];
        // Only treatment_item.id is the correct master TreatmentItem ID.
        return matched?.treatment_data?.treatment_item?.id ?? null;
    }, [leadDetail]);

    // Filter to groups whose template applies_to_statuses includes CONSULTATION_ATTENDED,
    // treatment match passes (generic or specific), AND have at least one task with checklist cells.
    const attendedGroups = useMemo(() => groups
            .map((g) => {
                const statuses = tplStatusMap.get(String(g.template_id)) || [];
                if (!statuses.includes(ATTENDED_STATUS)) return null;
                // 4th condition: treatment match
                const tplTreatmentItems = tplTreatmentsMap.get(String(g.template_id)) || [];
                const isGeneric = tplTreatmentItems.length === 0;
                const isTreatmentMatch = leadTreatmentId != null && tplTreatmentItems.some((ti) => String(ti?.id) === String(leadTreatmentId));
                if (!isGeneric && !isTreatmentMatch) return null;
                const tasksWithCells = (g.tasks || []).filter(
                    (task) => (task.checklist_items || []).length > 0
                );
                if (tasksWithCells.length === 0) return null;
                // Sort tasks by due_at (ascending; nulls last)
                const sorted = [...tasksWithCells].sort((a, b) => {
                    if (!a.due_at && !b.due_at) return 0;
                    if (!a.due_at) return 1;
                    if (!b.due_at) return -1;
                    return new Date(a.due_at) - new Date(b.due_at);
                });
                return { ...g, tasks: sorted };
            })
            .filter(Boolean), [groups, tplStatusMap, tplTreatmentsMap, leadTreatmentId]);

    const visible = leadInAttended && attendedGroups.length > 0;

    useEffect(() => {
        if (loading) return;
        if (onVisibilityChange) onVisibilityChange(visible);
    }, [visible, loading, onVisibilityChange]);

    const cardSx = {
        p: compact ? 1.5 : 2,
        mb: compact ? 0 : 2,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: 'none',
    };
    const headingSx = {
        fontWeight: 700,
        fontSize: compact ? '13px' : '0.8rem',
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
        color: 'text.secondary',
        mb: 1.5,
    };

    if (loading) {
        return (
            <Card sx={cardSx}>
                <Typography sx={headingSx}>Attended Checklist</Typography>
                <Stack spacing={1}>
                    {[1, 2].map((i) => (
                        <Skeleton key={i} variant="rounded" height={56} sx={{ borderRadius: 1.5 }} />
                    ))}
                </Stack>
            </Card>
        );
    }

    if (fetchError) {
        return (
            <Card sx={cardSx}>
                <Typography sx={headingSx}>Attended Checklist</Typography>
                <Alert severity="warning" action={<Button size="small" onClick={fetchAll}>Retry</Button>}>
                    {fetchError}
                </Alert>
            </Card>
        );
    }

    // Debug info — computed for superusers regardless of visibility.
    const debugInfo = isSuperuser ? {
        practiceId,
        leadId,
        featureAccess: hasFeatureAccess('tasks-v2', practiceId),
        leadInAttended,
        leadTreatmentId,
        leadTreatmentName: (leadDetail?.lead_treatments || [])
            .find((t) => t.status === ATTENDED_STATUS)?.treatment_data?.name || null,
        groupsFromApi: groups.length,
        templatesFromApi: templates.length,
        attendedTemplates: templates.filter((t) =>
            (Array.isArray(t.applies_to_statuses) ? t.applies_to_statuses : []).includes(ATTENDED_STATUS)
        ).length,
        templatesMatchingTreatment: templates.filter((t) => {
            const statuses = Array.isArray(t.applies_to_statuses) ? t.applies_to_statuses : [];
            if (!statuses.includes(ATTENDED_STATUS)) return false;
            const tItems = Array.isArray(t.applies_to_treatment_items) ? t.applies_to_treatment_items : [];
            if (tItems.length === 0) return false; // generic — counted separately
            return leadTreatmentId != null && tItems.some((ti) => String(ti?.id) === String(leadTreatmentId));
        }).length,
        genericTemplates: templates.filter((t) => {
            const statuses = Array.isArray(t.applies_to_statuses) ? t.applies_to_statuses : [];
            return statuses.includes(ATTENDED_STATUS) &&
                (Array.isArray(t.applies_to_treatment_items) ? t.applies_to_treatment_items : []).length === 0;
        }).length,
        attendedGroupsCount: attendedGroups.length,
        visible,
    } : null;

    const debugOverlay = isSuperuser ? (
        <Accordion
            disableGutters
            elevation={0}
            data-testid="attended-checklist-v2-debug"
            sx={{
                mt: 1,
                bgcolor: '#FAFAFA',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: '8px !important',
                '&:before': { display: 'none' },
            }}
        >
            <AccordionSummary sx={{ minHeight: 32, '& .MuiAccordionSummary-content': { my: 0.5 } }}>
                <Typography sx={{ fontSize: 11, fontFamily: 'monospace', color: '#6e6e73' }}>
                    🔧 V2 Visibility Debug (superadmin only)
                </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
                <Box
                    component="pre"
                    sx={{
                        fontSize: 11,
                        fontFamily: 'monospace',
                        color: '#6e6e73',
                        m: 0,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-all',
                    }}
                >
                    {JSON.stringify(debugInfo, null, 2)}
                </Box>
            </AccordionDetails>
        </Accordion>
    ) : null;

    // Visibility gate — render nothing (except debug overlay for superusers).
    if (!visible) return debugOverlay;

    return (
        <>
        <Card sx={cardSx}>
            <Typography sx={headingSx}>Attended Checklist</Typography>
            <Stack spacing={1.5}>
                {attendedGroups.map((group) => {
                    const { completed = 0, total = 0 } = group.progress || {};
                    const pct = total > 0 ? (completed / total) * 100 : 0;
                    return (
                        <Box
                            key={`${group.template_id}-${group.applied_at || ''}`}
                            sx={{
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 1.5,
                                p: 1.25,
                            }}
                        >
                            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 0.5 }}>
                                <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.85rem' }}>
                                    {group.template_name}
                                </Typography>
                                <Chip
                                    label={`${completed} / ${total}`}
                                    size="small"
                                    sx={{
                                        height: 20,
                                        fontSize: '0.7rem',
                                        bgcolor: alpha(primary.main, 0.1),
                                        color: pct === 100 ? 'success.main' : primary.main,
                                        fontWeight: 600,
                                    }}
                                />
                            </Stack>
                            {total > 0 && (
                                <LinearProgress
                                    variant="determinate"
                                    value={pct}
                                    sx={{
                                        height: 5,
                                        borderRadius: 3,
                                        mb: 1,
                                        bgcolor: 'rgba(0,0,0,0.08)',
                                        '& .MuiLinearProgress-bar': {
                                            borderRadius: 3,
                                            bgcolor: pct === 100 ? '#00AB55' : primary.main,
                                        },
                                    }}
                                />
                            )}
                            <Stack spacing={1.25} sx={{ mt: 1 }}>
                                {group.tasks.map((task, taskIdx) => (
                                    <Box key={task.id}>
                                        {taskIdx > 0 && <Divider sx={{ my: 1 }} />}
                                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                                            <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.78rem', color: 'text.primary' }}>
                                                {task.title}
                                            </Typography>
                                            {task.due_at && (
                                                <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.disabled' }}>
                                                    · due {new Date(task.due_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                                                </Typography>
                                            )}
                                        </Stack>
                                        <Stack spacing={0.5}>
                                            {(task.checklist_items || []).map((cell) => (
                                                <ChecklistItemRow
                                                    key={cell.id || cell.checklist_item_id}
                                                    item={cell}
                                                    practiceId={practiceId}
                                                    taskId={task.id}
                                                    leadData={leadDetail || { id: leadId }}
                                                    onDone={fetchAll}
                                                />
                                            ))}
                                        </Stack>
                                    </Box>
                                ))}
                            </Stack>
                        </Box>
                    );
                })}
            </Stack>
        </Card>
        {debugOverlay}
        </>
    );
}

AttendedChecklistV2.propTypes = {
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    leadId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    leadDetail: PropTypes.object,
    compact: PropTypes.bool,
    onVisibilityChange: PropTypes.func,
};

AttendedChecklistV2.defaultProps = {
    leadDetail: null,
    compact: false,
    onVisibilityChange: null,
};
